import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Trophy, Crown, Bell, Loader2, X, Star, Search, SlidersHorizontal, Check, Clock, Radio, Sparkles, Users, Gift } from 'lucide-react';
import { Virtuoso } from 'react-virtuoso';
import { API } from '../config';
import PullToRefresh from '../components/PullToRefresh';
import { useAuth } from '../contexts/AuthContext';
import { useMatches, useLiveScores } from '../hooks/useQueries';
import BottomNav from '../components/BottomNav';
import MatchRow from '../components/MatchRow';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { GoogleReferralCodeModal, CardQuotaReachedModal, InviteFriendsModal } from '../components/ReferralModals';
import LeagueHeader from '../components/LeagueHeader';
import { MatchSkeleton } from '../components/SkeletonLoaders';
import LegalFooter from '../components/LegalFooter';
import EmailVerifyBanner from '../components/EmailVerifyBanner';
import NotificationReengagementBanner from '../components/NotificationReengagementBanner';
import { VipTodayBanner } from '../components/VipTodayBanner';
import { AfterPriorityPopups } from '../components/consent/PriorityPopups';
import { useMySelection } from '../hooks/useMySelection';
import MySelectionButton from '../components/mySelection/MySelectionButton';
import { PENDING_SHARE_KEY } from './SharedSelectionPage';
import FavoriteTeamCard from '../components/FavoriteTeamCard';
import MySelectionList from '../components/mySelection/MySelectionList';
import StarChoiceModal from '../components/mySelection/StarChoiceModal';
import NotificationsOffBar from '../components/NotificationsOffBar';
import { useMatchFollows } from '../hooks/useMatchFollows';
import NotifyAskModal from '../components/mySelection/NotifyAskModal';
import MySelectionTour from '../components/mySelection/MySelectionTour';
import { DEMO_SELECTION } from '../components/mySelection/demoSelection';
import useTutorialStatus from '../hooks/useTutorialStatus';
import { getPushPermissionState } from '../services/pushService';
import { usePriorityPopupBlocking } from '../components/consent/priorityPopupStore';
import NotificationProofOfLossModal from '../components/NotificationProofOfLossModal';
import { requestNotificationPermission, isNotificationSupported, isNativePlatform, getPushPlatform } from '../services/pushService';
import { getCachedImage, setCachedImage } from '../lib/imageCache';
import { italyToDate } from '../lib/italyTime';
import {
  getLeagueKey,
  getLeagueDisplay,
  buildLeagueGroups,
  getLeagueTopOverrides,
} from '../lib/leagues';

// Countries included in "Principali" filter
const MAIN_COUNTRIES = [
  // Europe
  'Italy', 'England', 'Germany', 'France', 'Spain', 'Netherlands', 'Portugal',
  'Belgium', 'Scotland', 'Turkey', 'Switzerland', 'Austria', 'Greece', 'Poland',
  'Ukraine', 'Russia', 'Czech-Republic', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Croatia', 'Serbia', 'Romania', 'Bulgaria', 'Hungary', 'Cyprus', 'Israel',
  'Ireland', 'Northern-Ireland', 'Wales', 'Iceland', 'Slovenia', 'Slovakia',
  'Bosnia', 'Bosnia-And-Herzegovina', 'Albania', 'North-Macedonia', 'Montenegro',
  'Kosovo', 'Malta', 'Luxembourg', 'Liechtenstein', 'Andorra', 'San-Marino',
  'Faroe-Islands', 'Gibraltar', 'Estonia', 'Latvia', 'Lithuania', 'Belarus',
  'Moldova', 'Georgia', 'Armenia', 'Azerbaijan', 'Kazakhstan', 'Europe',
  // Americas
  'Brazil', 'Argentina', 'USA', 'Mexico', 'Colombia', 'Chile', 'Uruguay',
  'Paraguay', 'Peru', 'Ecuador', 'Bolivia', 'Venezuela',
];

// International / "World" competitions included in "Principali" filter
const MAIN_COMPETITIONS = [
  // UEFA
  'UEFA Champions League', 'Champions League', 'UEFA Europa League', 'Europa League',
  'UEFA Europa Conference League', 'Conference League', 'UEFA Nations League', 'Nations League',
  'UEFA Super Cup', 'Super Cup',
  'UEFA U21 Championship',
  'Euro Championship', 'European Championship',
  // World Cup & qualifiers
  'World Cup - Qualification Europe', 'World Cup - Qualification South America',
  'World Cup', 'FIFA Club World Cup',
  // Americas
  'Copa Libertadores', 'Copa Sudamericana', 'Recopa Sudamericana',
  'Copa America', 'CONMEBOL',
  'MLS', 'Major League Soccer',
  'Serie A', 'Serie B',
  // International
  'Friendlies',
];

const FAVORITES_KEY = 'rvincente_favorites';

const loadFavorites = () => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    if (!stored) return {};
    const favorites = JSON.parse(stored);
    const kickoffTimes = Object.values(favorites).filter(Boolean);
    if (kickoffTimes.length === 0) return {};
    const latestKickoff = new Date(Math.max(...kickoffTimes.map(t => new Date(t).getTime())));
    const now = new Date();
    if (now.getTime() - latestKickoff.getTime() > 3 * 60 * 60 * 1000) {
      localStorage.removeItem(FAVORITES_KEY);
      return {};
    }
    return favorites;
  } catch { return {}; }
};

const saveFavorites = (favorites) => {
  const filtered = Object.fromEntries(Object.entries(favorites).filter(([_, v]) => v));
  if (Object.keys(filtered).length === 0) localStorage.removeItem(FAVORITES_KEY);
  else localStorage.setItem(FAVORITES_KEY, JSON.stringify(filtered));
};

// Translate country code to Italian label / Top-league rules / sort helpers
// are imported from `lib/leagues.js` — single source of truth shared with
// IAPage. Do NOT redefine them here.

// Get current date in Italy timezone (CET/CEST)
const LIVE_SHORT = ['1H', '2H', 'HT', 'ET', 'P', 'BT', 'LIVE'];
const FINAL_FIELDS = ['status', 'is_finished', 'ht_score_home', 'ht_score_away', 'penalty_home',
  'penalty_away', 'extratime_home', 'extratime_away', 'winner_side'];


const getItalyDate = (offsetDays = 0) => {
  const now = new Date();
  // Get Italy time using Intl
  const italyStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' }); // YYYY-MM-DD format
  const italyDate = new Date(italyStr + 'T12:00:00'); // noon to avoid DST edge cases
  italyDate.setDate(italyDate.getDate() + offsetDays);
  return italyDate.toISOString().split('T')[0];
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showMySelection, setShowMySelection] = useState(false);
  const [importQuotaOpen, setImportQuotaOpen] = useState(false);
  const [importInviteOpen, setImportInviteOpen] = useState(false);
  const [starChoiceId, setStarChoiceId] = useState(null);
  const matchFollows = useMatchFollows(!!user);
  const mySel = useMySelection(!!user, showMySelection, () => setImportQuotaOpen(true));
  const [notifyAsk, setNotifyAsk] = useState(null);
  const askNotify = async (fromToggle = false) => {
    const state = await getPushPermissionState().catch(() => 'unsupported');
    if (state === 'granted' || state === 'prompt') setNotifyAsk(state);
    else if (fromToggle) toast.error('Notifiche bloccate: attivale dalle impostazioni del telefono o del browser.');
  };
  const importMySelection = async (file) => {
    if (await mySel.importFile(file)) { setShowMySelection(true); askNotify(); }
  };
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('my_selection') && mySel.selection) setShowMySelection(true);
  }, [mySel.selection]);

  // Tour "La mia selezione": push link /dashboard?tour=my_selection or the announcement modal.
  const { markSeen: markMySelTourSeen } = useTutorialStatus('my_selection');
  const [mySelTour, setMySelTour] = useState(false);
  useEffect(() => {
    const start = () => { setShowMySelection(true); setTimeout(() => setMySelTour(true), 300); };
    if (new URLSearchParams(window.location.search).get('tour') === 'my_selection') start();
    window.addEventListener('startMySelectionTour', start);
    return () => window.removeEventListener('startMySelectionTour', start);
  }, []);
  const finishMySelTour = () => {
    setMySelTour(false);
    setShowMySelection(!!mySel.selection);
    markMySelTourSeen();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!user) navigate('/login?tab=register');
  };
  const shownSelection = mySelTour ? DEMO_SELECTION : mySel.selection;
  // Stable VIP flag — passed to all MatchRow children so they don't all
  // need to call useAuth() each render (which would break their React.memo
  // every time the user object reference changes — even on harmless
  // last_active throttle updates).
  const isVip = !!(user && user.is_vip);
  const isPremium = !!(user && (user.is_vip || user.is_gold || user.is_admin));
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('saved') === '1') navigate('/diario?saved=1', { replace: true });
  }, [navigate]);
  useEffect(() => {
    const pending = user && localStorage.getItem(PENDING_SHARE_KEY);
    if (pending) navigate(`/s/${pending}`);
  }, [user, navigate]);
  // iter252 — Referral progress badge below the user's name. Shows
  // "X di 3 amici invitati" (progress) or "Cartellini sbloccati · Ngg"
  // (unlocked). Click → navigate to /bonus. Reduces chat requests
  // ("ho invitato 3 amici ma non vedo lo sblocco") by making the real
  // counter self-explanatory.
  const [referralStatus, setReferralStatus] = useState(null);
  useEffect(() => {
    if (!user?.user_id) return;
    // VIP users already have everything unlocked — skip the fetch to
    // save a round-trip (badge is pointless for them).
    if (user.is_vip || user.is_admin) return;
    let alive = true;
    axios.get(`${API}/referral/me`).then((r) => {
      if (alive) setReferralStatus(r.data);
    }).catch(() => { /* non-blocking — badge just won't render */ });
    return () => { alive = false; };
  }, [user?.user_id, user?.is_vip, user?.is_admin]);
  // Notification soft-ask: trigger on first match click (a clear "value
  // moment"). Once triggered for the session we keep the flag latched to
  // true so the hook can re-evaluate after the modal closes.
  const priorityBlocking = usePriorityPopupBlocking();
  const [selectedDate, setSelectedDate] = useState(() => {
    // Restore last viewed date so "back" from match detail keeps the user
    // on the same day instead of resetting to today.
    try {
      const saved = sessionStorage.getItem('dashboard_selected_date');
      if (saved) return saved;
    } catch { /* sessionStorage unavailable (SSR/privacy mode) */ }
    return getItalyDate(0);
  });
  const { data: rawMatches = [], isLoading: loading, error: matchesQueryError, refetch: refetchMatches } = useMatches(selectedDate);
  // iter262 (2026-10-06): fast live-score merge. Backend fetches
  // API-Football `/fixtures?live=all` (cached 8s). Frontend polls every
  // 10s and overlays fresh scores/minute on top of the heavy useMatches
  // payload (polled every 90s). Result: ~10-15s latency like diretta.it.
  // We only enable the hook when the date is "today" OR we actually see
  // at least one live match in the current payload, to avoid wasting
  // calls on past/future dates.
  const hasAnyLive = Array.isArray(rawMatches)
    && rawMatches.some(m => m && m.is_live);
  const liveEnabled = hasAnyLive || (selectedDate === getItalyDate(0));
  const { data: liveScores } = useLiveScores(liveEnabled);
  // Merge live scores on top of rawMatches
  const matches = useMemo(() => {
    if (!Array.isArray(rawMatches) || rawMatches.length === 0) return [];
    const byId = liveScores?.byFixtureId || {};
    if (!byId || Object.keys(byId).length === 0) return rawMatches;
    return rawMatches.map(m => {
      const live = byId[m.fixture_id];
      if (!live) return m;
      if (m.is_finished && !live.ended_at) return m;
      // Overlay fresh live data (or the final state of a just-ended match)
      const merged = {
        ...m,
        is_live: LIVE_SHORT.includes(live.status_short),
        status_short: live.status_short || m.status_short,
        elapsed: live.elapsed ?? m.elapsed,
        score_home: live.home_score ?? m.score_home,
        score_away: live.away_score ?? m.score_away,
        home_red_cards: live.home_red ?? m.home_red_cards,
        away_red_cards: live.away_red ?? m.away_red_cards,
        live_badges: live.live_badges || m.live_badges,
      };
      if (live.ended_at) {
        FINAL_FIELDS.forEach((k) => { if (live[k] !== undefined) merged[k] = live[k]; });
      } else if (merged.is_live && !m.is_live) {
        merged.status = live.status_short;
      }
      return merged;
    });
  }, [rawMatches, liveScores]);
  // Shown as LIVE but missing from the live feed for 2 polls in a row → reload the day list.
  const missStreak = useRef(0);
  const lastForcedRefetch = useRef(0);
  useEffect(() => {
    if (!liveScores || !Array.isArray(rawMatches)) return;
    const byId = liveScores.byFixtureId || {};
    const orphan = rawMatches.some(m => m?.is_live && !byId[m.fixture_id]);
    missStreak.current = orphan ? missStreak.current + 1 : 0;
    if (missStreak.current >= 2 && Date.now() - lastForcedRefetch.current > 60000) {
      lastForcedRefetch.current = Date.now();
      missStreak.current = 0;
      refetchMatches();
    }
  }, [liveScores, rawMatches, refetchMatches]);
  const matchesError = matchesQueryError ? `Errore caricamento: ${matchesQueryError?.message || 'Connessione fallita'}` : null;
  const [showOnlyTopLeagues, setShowOnlyTopLeagues] = useState(() => {
    // Persist across navigations. Default true = "Principali" attivo per i
    // nuovi utenti; una volta deselezionato dall'utente resta ricordato.
    try {
      const raw = localStorage.getItem('dashboard_only_top_leagues');
      if (raw === '0') return false;
      if (raw === '1') return true;
      return true;
    } catch { return true; }
  });
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(null);
  const [requestingPermission, setRequestingPermission] = useState(false);
  const [favorites, setFavorites] = useState(loadFavorites);
  const [showFavorites, setShowFavorites] = useState(false);
  // New filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showLeagueFilter, setShowLeagueFilter] = useState(false);
  const [deselectedLeagues, setDeselectedLeagues] = useState(new Set());
  // iter239 — popup codice amico post-Google-login (saltabile)
  const [showReferralPopup, setShowReferralPopup] = useState(() => {
    try { return localStorage.getItem('pendingReferralPopup') === '1'; } catch { return false; }
  });
  // Chronological sort + Live-only filter (persisted)
  const [sortByTime, setSortByTime] = useState(() => {
    try { return localStorage.getItem('dashboard_sort_by_time') === '1'; } catch { return false; }
  });
  const [showOnlyLive, setShowOnlyLive] = useState(() => {
    try { return localStorage.getItem('dashboard_only_live') === '1'; } catch { return false; }
  });
  // "Solo Non Iniziate" toggle (2026-02): fastest way to hide LIVE + FT
  // matches and see only what's still bettable. Mutually exclusive with
  // showOnlyLive to avoid a contradictory "solo live E solo non iniziate".
  const [showOnlyUpcoming, setShowOnlyUpcoming] = useState(() => {
    try { return localStorage.getItem('dashboard_only_upcoming') === '1'; } catch { return false; }
  });
  // AI-based filters: bet type (1/X/2/Goal/No Goal/Over 2.5/Under 2.5) and
  // confidence (Alta/Media/Bassa). When any of these is active, the
  // Dashboard fetches predictions in bulk for the visible matches and
  // keeps only those matching the criteria.
  // 2026-02: persist filters in sessionStorage so that navigating into a
  // match detail and pressing "indietro" restores them (user feedback
  // "tornando indietro devi riselezionare nuovamente il filtro").
  const [aiFilterBetTypes, setAiFilterBetTypes] = useState(() => {
    try {
      const raw = sessionStorage.getItem('dashboard_ai_filter_bet_types');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();
  }); // Set<string>
  const [aiFilterConfidence, setAiFilterConfidence] = useState(() => {
    try {
      const raw = sessionStorage.getItem('dashboard_ai_filter_confidence');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();
  }); // Set<"Alta"|"Media"|"Bassa">
  const [showAiFilterPanel, setShowAiFilterPanel] = useState(false);
  const [predictionsMap, setPredictionsMap] = useState(new Map()); // fixture_id → {prediction, confidence}
  const [loadingAiFilter, setLoadingAiFilter] = useState(false);
  // Leagues the user has manually toggled (overrides default open/closed
  // state). Persisted in sessionStorage so that back-navigating from a
  // match detail restores the exact same accordion layout — otherwise the
  // shorter page would land the scroll restore at the bottom of the
  // dashboard instead of the user's original position (2026-07-17 fix).
  const [collapsedOverrides, setCollapsedOverrides] = useState(() => {
    try {
      const raw = sessionStorage.getItem('dashboard_collapsed_overrides');
      if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return new Set();
  });
  const [expandedOverrides, setExpandedOverrides] = useState(() => {
    try {
      const raw = sessionStorage.getItem('dashboard_expanded_overrides');
      if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return new Set();
  });
  useEffect(() => {
    try {
      sessionStorage.setItem('dashboard_collapsed_overrides', JSON.stringify([...collapsedOverrides]));
      sessionStorage.setItem('dashboard_expanded_overrides', JSON.stringify([...expandedOverrides]));
    } catch {}
  }, [collapsedOverrides, expandedOverrides]);
  // Persist AI filters so back-navigation from match detail restores them.
  useEffect(() => {
    try {
      sessionStorage.setItem('dashboard_ai_filter_bet_types', JSON.stringify([...aiFilterBetTypes]));
      sessionStorage.setItem('dashboard_ai_filter_confidence', JSON.stringify([...aiFilterConfidence]));
    } catch { /* ignore */ }
  }, [aiFilterBetTypes, aiFilterConfidence]);
  useEffect(() => {
    try { localStorage.setItem('dashboard_sort_by_time', sortByTime ? '1' : '0'); } catch {}
  }, [sortByTime]);
  useEffect(() => {
    try { localStorage.setItem('dashboard_only_live', showOnlyLive ? '1' : '0'); } catch {}
  }, [showOnlyLive]);
  useEffect(() => {
    try { localStorage.setItem('dashboard_only_upcoming', showOnlyUpcoming ? '1' : '0'); } catch {}
  }, [showOnlyUpcoming]);
  useEffect(() => {
    // Persist the "Principali/Tutte" toggle across navigations (e.g. entering
    // a match detail and coming back). Fix per feedback utente 22/08/2026.
    try { localStorage.setItem('dashboard_only_top_leagues', showOnlyTopLeagues ? '1' : '0'); } catch {}
  }, [showOnlyTopLeagues]);

  // Bulk-fetch AI predictions when at least one AI filter is active.
  // Runs once per (matches list × filter activation) — the map is kept
  // populated so the filter feels instant when toggling on/off.
  const aiFiltersActive = aiFilterBetTypes.size > 0 || aiFilterConfidence.size > 0;
  useEffect(() => {
    if (!aiFiltersActive || !matches?.length) return;
    // Skip finished matches — no point fetching predictions for them,
    // they're going to be hidden by the filter anyway (2026-02 fix).
    const finishedStatuses = ["FT", "AET", "PEN", "FT_PEN", "Full Time", "Terminata", "Finished", "After Extra Time", "Dopo supplementari", "Ai rigori"];
    const isFinished = (m) => m.is_finished || finishedStatuses.includes(m.status) || finishedStatuses.includes(m.status_short);
    // Only fetch predictions we don't already have cached in state.
    const missingIds = matches
      .filter(m => !isFinished(m))
      .map(m => m.fixture_id)
      .filter(id => id && !predictionsMap.has(id));
    if (missingIds.length === 0) return;
    let cancelled = false;
    setLoadingAiFilter(true);
    (async () => {
      try {
        // Chunk to stay under the server-side 500 IDs cap.
        const chunkSize = 400;
        const all = [];
        for (let i = 0; i < missingIds.length; i += chunkSize) {
          const chunk = missingIds.slice(i, i + chunkSize);
          const { data } = await axios.get(`${API}/predictions/bulk`, {
            params: { fixture_ids: chunk.join(',') },
            timeout: 20000,
          });
          if (Array.isArray(data)) all.push(...data);
          if (cancelled) return;
        }
        if (cancelled) return;
        setPredictionsMap(prev => {
          const next = new Map(prev);
          all.forEach(p => next.set(p.fixture_id, { prediction: p.prediction, confidence: p.confidence }));
          return next;
        });
      } catch {
        // Silent — filter simply shows empty until user retries or disables.
      } finally {
        if (!cancelled) setLoadingAiFilter(false);
      }
    })();
    return () => { cancelled = true; };
  }, [aiFiltersActive, matches, predictionsMap]);
  const searchInputRef = useRef(null);
  const filterRef = useRef(null);

  const favoritesCount = Object.keys(favorites).length;

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showLeagueFilter) return;
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowLeagueFilter(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [showLeagueFilter]);

  // Focus search input when opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) searchInputRef.current.focus();
  }, [showSearch]);

  // Reset league filter when date/region changes
  useEffect(() => { setDeselectedLeagues(new Set()); }, [selectedDate, showOnlyTopLeagues]);

  // Prefetch lazy chunks for adjacent tabs (VIP, Bonus, Chat, IA) after Dashboard mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      import('../pages/VIPPage').catch(() => {});
      import('../pages/BonusPage').catch(() => {});
      import('../pages/ChatPage').catch(() => {});
      import('../pages/IAPage').catch(() => {});
    }, 3000); // 3s delay to not compete with initial data loading
    return () => clearTimeout(timer);
  }, []);

  // Handle app resume: refetch matches data
  useEffect(() => {
    const handleAppResume = (event) => {
      const { pausedMs } = event.detail || {};
      if (pausedMs > 30000) {
        refetchMatches();
      }
    };
    window.addEventListener('appResume', handleAppResume);
    return () => window.removeEventListener('appResume', handleAppResume);
  }, [refetchMatches]);

  // Pre-warm VIP prediction images into IndexedDB cache.
  // When the user visits /vip the images will render INSTANTLY from blob cache.
  // Silent (ignores errors), low priority — runs 2s after mount.
  useEffect(() => {
    if (!user) return;
    const timer = setTimeout(async () => {
      try {
        const { data: preds } = await axios.get(`${API}/vip-predictions`, { timeout: 10000 });
        if (!Array.isArray(preds)) return;
        const token = localStorage.getItem('auth_token');
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const cb = Math.floor(Date.now() / (24 * 60 * 60 * 1000));
        for (const pred of preds) {
          if (!pred?.image_url) continue;
          const fullUrl = pred.image_url.startsWith('http')
            ? pred.image_url
            : `${BACKEND_URL}${pred.image_url}`;
          const cached = await getCachedImage(fullUrl);
          if (cached) continue; // already in cache
          try {
            const sep = fullUrl.includes('?') ? '&' : '?';
            const resp = await fetch(`${fullUrl}${sep}cb=${cb}${token ? '&token=' + token : ''}`);
            if (resp.ok) {
              const blob = await resp.blob();
              setCachedImage(fullUrl, blob).catch(() => {});
            }
          } catch {}
        }
      } catch {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [user]);


  useEffect(() => {
    if (!user || !isNotificationSupported() || isNativePlatform()) return;
    const dismissed = localStorage.getItem('notification_modal_dismissed');
    if (dismissed) return;
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'default';
    setNotificationPermission(permission);
  }, [user]);

  const handleEnableNotifications = async () => {
    setRequestingPermission(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await axios.post(`${API}/notifications/register-token`, { token, platform: getPushPlatform() });
        setNotificationPermission('granted');
        setShowNotificationModal(false);
        localStorage.setItem('notification_modal_dismissed', 'true');
      } else {
        setNotificationPermission(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
      }
    } catch (error) { console.error('Error requesting notification permission:', error); }
    setRequestingPermission(false);
  };

  const dismissNotificationModal = () => { setShowNotificationModal(false); localStorage.setItem('notification_modal_dismissed', 'true'); };

  useEffect(() => {
    const interval = setInterval(() => {
      const fresh = loadFavorites();
      if (Object.keys(fresh).length !== Object.keys(favorites).length) {
        setFavorites(fresh);
        if (Object.keys(fresh).length === 0 && showFavorites) setShowFavorites(false);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [favorites, showFavorites]);

  const toggleFavorite = useCallback((fixtureId) => {
    setFavorites(prev => {
      const next = { ...prev };
      if (next[fixtureId]) {
        delete next[fixtureId];
      } else {
        const match = matches.find(m => m.fixture_id === fixtureId);
        next[fixtureId] = (match && italyToDate(match.match_date, match.match_time || '00:00')?.toISOString()) || new Date().toISOString();
      }
      saveFavorites(next);
      if (Object.keys(next).length === 0) setShowFavorites(false);
      return next;
    });
  }, [matches]);

  // With an active selection the star asks: add to "La mia selezione" or to favourites.
  const onStarTap = useCallback((fixtureId) => {
    if (mySel.selection && !favorites[fixtureId]) setStarChoiceId(fixtureId);
    else toggleFavorite(fixtureId);
  }, [mySel.selection, favorites, toggleFavorite]);

  // Build available leagues list from region-filtered matches
  const regionFiltered = useMemo(() => {
    if (!showOnlyTopLeagues) return matches;
    const topOverrides = getLeagueTopOverrides();
    return matches.filter(m => {
      // 2026-08-05 (user request): ANY explicit admin choice — APERTA (true)
      // OR CHIUSA (false) — includes the league in the Home feed. The chip
      // now controls ONLY the accordion default-open state, not visibility.
      // Semantic: "if I ordered it in the priority panel, users must see it".
      // Only leagues left on AUTO (no override) go through the youth/women/
      // reserve keyword filter and the MAIN_COUNTRIES/MAIN_COMPETITIONS
      // allowlist.
      const lid = m.league_id;
      if (lid != null) {
        const override = topOverrides[String(lid)];
        if (override === true || override === false) return true;
      }

      // Block youth, women, reserves, amateur leagues from "Principali"
      // EXCEPTION 2026-08-05 (user request): any league whose name contains
      // "champions league" is ALWAYS allowed even if the name also matches
      // youth/women/reserve keywords (e.g. UEFA Youth League / UEFA Women's
      // Champions League). These are top-tier competitions the user wants
      // to see in the main feed by default.
      const ln = (m.league_name || '').toLowerCase();
      const isChampionsLeague = ln.includes('champions league');
      if (!isChampionsLeague && (ln.includes('youth') || ln.includes('u19') || ln.includes('u17') || ln.includes('u16') || ln.includes('women') || ln.includes('reserve') || ln.includes('amateur'))) return false;
      if (MAIN_COUNTRIES.includes(m.league_country)) return true;
      if (m.league_country === 'World' && MAIN_COMPETITIONS.some(comp => m.league_name?.includes(comp))) return true;
      if (isChampionsLeague) return true;
      return false;
    });
  }, [matches, showOnlyTopLeagues]);

  // Available leagues for the filter dropdown — sorted by count desc, then name
  // so the heaviest leagues appear at the top and niche ones (e.g. Norwegian
  // 1. Division) can be found scrolling through a predictable list instead
  // of a random insertion order.
  const availableLeagues = useMemo(() => {
    const map = new Map();
    regionFiltered.forEach(m => {
      const key = getLeagueKey(m);
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: getLeagueDisplay(m.league_name || 'Altro', m.league_country || ''),
          logo: m.league_logo,
          count: 0
        });
      }
      map.get(key).count++;
    });
    return Array.from(map.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name);
    });
  }, [regionFiltered]);

  const activeLeagueFilterCount = deselectedLeagues.size;

  // Shared helper: is a match finished? Uses the same status list as
  // MatchRow.jsx / MatchDetail.jsx so behaviour is consistent.
  const FINISHED_STATUSES = ["FT", "AET", "PEN", "FT_PEN", "Full Time", "Terminata", "Finished", "After Extra Time", "Dopo supplementari", "Ai rigori"];
  const isMatchFinished = (m) => m.is_finished || FINISHED_STATUSES.includes(m.status) || FINISHED_STATUSES.includes(m.status_short);

  // Apply all filters: region → league → search → favorites
  const filteredMatches = useMemo(() => {
    let result = regionFiltered;

    // League filter
    if (deselectedLeagues.size > 0) {
      result = result.filter(m => !deselectedLeagues.has(getLeagueKey(m)));
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(m =>
        m.home_team?.toLowerCase().includes(q) ||
        m.away_team?.toLowerCase().includes(q)
      );
    }

    // Favorites filter
    if (showFavorites) {
      result = result.filter(m => favorites[m.fixture_id]);
    }

    // Live-only filter
    if (showOnlyLive) {
      result = result.filter(m => m.is_live);
    }

    // "Solo Non Iniziate" — user wants to see only bets they can still
    // place. Hides both LIVE and FINISHED matches (NS/TBD/PST only).
    if (showOnlyUpcoming) {
      result = result.filter(m => !m.is_live && !isMatchFinished(m));
    }

    // AI filters (bet type / confidence). Applied only when at least one is
    // active AND the prediction cache is loaded for the fixture (otherwise
    // we hide the match — the effect below fetches predictions in bulk when
    // filters become active).
    // 2026-02: also exclude finished matches when any AI filter is active —
    // filtering a past FT match by "Over 2.5" doesn't make sense and confuses
    // users looking for upcoming bets (screenshot feedback 25/08).
    const hasBetTypeFilter = aiFilterBetTypes.size > 0;
    const hasConfFilter = aiFilterConfidence.size > 0;
    if (hasBetTypeFilter || hasConfFilter) {
      result = result.filter(m => {
        if (isMatchFinished(m)) return false;
        const p = predictionsMap.get(m.fixture_id);
        if (!p) return false; // no prediction available → drop
        if (hasBetTypeFilter && !aiFilterBetTypes.has(p.prediction)) return false;
        if (hasConfFilter && !aiFilterConfidence.has(p.confidence)) return false;
        return true;
      });
    }

    return result;
  }, [regionFiltered, deselectedLeagues, searchQuery, showFavorites, favorites, showOnlyLive, showOnlyUpcoming, aiFilterBetTypes, aiFilterConfidence, predictionsMap]);

  // Breakdown for the "22 su 159" summary line — count reasons a match
  // was excluded so the user understands the delta (2026-02 UX).
  const filterBreakdown = useMemo(() => {
    if (!aiFiltersActive && !showOnlyUpcoming) return null;
    let finished = 0;
    let live = 0;
    for (const m of regionFiltered) {
      if (isMatchFinished(m)) finished++;
      else if (m.is_live) live++;
    }
    return { finished, live };
  }, [regionFiltered, aiFilterBetTypes, aiFilterConfidence, showOnlyUpcoming]);


  // Group matches by league using the shared helper (same logic as IAPage).
  // Top leagues sort first (expanded), bucket priority = best league in
  // that country, internal order from TOP_LEAGUE_RULES, etc.
  const leagueGroups = useMemo(
    () => buildLeagueGroups(filteredMatches).map(g => ({
      ...g,
      // Dashboard expects `name` to be the user-facing display label
      // (e.g. "Bundesliga (Germania)"). Keep the raw name accessible via
      // `rawName` in case other callers need it.
      rawName: g.name,
      name: g.displayName,
    })),
    [filteredMatches]
  );

  // Persist selected date so back-navigation from match detail keeps it.
  useEffect(() => {
    try { sessionStorage.setItem('dashboard_selected_date', selectedDate); } catch {}
  }, [selectedDate]);

  // Stable callback for match clicks (avoids breaking React.memo).
  // Virtuoso ref: usato per salvare/ripristinare lo stato scroll in modo
  // preciso via API `getState()` + `restoreStateFrom`, invece del vecchio
  // window.scrollTo iterativo che con la virtualizzazione era impreciso
  // (Virtuoso monta le row progressivamente).
  const virtuosoRef = useRef(null);
  // Stato Virtuoso ripristinato al mount. Letto una sola volta da
  // sessionStorage così cambiamenti successivi non lo rileggono.
  const [restoredVirtuosoState, setRestoredVirtuosoState] = useState(() => {
    try {
      const raw = sessionStorage.getItem('dashboard_virtuoso_state');
      const savedDate = sessionStorage.getItem('dashboard_scroll_date');
      const currentDate = sessionStorage.getItem('dashboard_selected_date') || '';
      // Se lo snapshot appartiene a un'altra data, ignoralo (non pulire ora,
      // ci pensa l'effetto sotto).
      if (raw && savedDate && (!currentDate || savedDate === currentDate)) {
        return JSON.parse(raw);
      }
    } catch { /* ignore */ }
    return null;
  });
  const handleMatchClick = useCallback((fixtureId) => {
    // Save the exact Virtuoso state (including item sizes + scroll offset)
    // via the ref API. This is FAR more reliable than window.scrollY+retry
    // because Virtuoso mounts items progressively; a raw scrollY may land
    // on a different item after remount if intermediate row heights change.
    try {
      if (virtuosoRef.current && typeof virtuosoRef.current.getState === 'function') {
        virtuosoRef.current.getState((state) => {
          try {
            sessionStorage.setItem('dashboard_virtuoso_state', JSON.stringify(state));
            sessionStorage.setItem('dashboard_scroll_date', selectedDate);
            sessionStorage.setItem('dashboard_selected_date', selectedDate);
          } catch { /* quota exceeded / disabled */ }
        });
      }
      // Legacy fallback (used only if virtuosoRef isn't available yet)
      sessionStorage.setItem('dashboard_scroll_y', String(window.scrollY));
      sessionStorage.setItem('dashboard_scroll_date', selectedDate);
    } catch { /* ignore */ }
    navigate(`/match/${fixtureId}`);
  }, [navigate, selectedDate]);

  // Flat list for virtualized rendering
  const flatItems = useMemo(() => {
    const items = [];
    // Chronological (flat) view: sort all matches by match_time and skip league headers.
    // Live matches are always placed on top (they're the most urgent).
    if (sortByTime || showOnlyLive) {
      const sorted = [...filteredMatches].sort((a, b) => {
        // Live first
        if (a.is_live && !b.is_live) return -1;
        if (!a.is_live && b.is_live) return 1;
        // Then by match time (HH:MM)
        const ta = a.match_time || '99:99';
        const tb = b.match_time || '99:99';
        if (ta !== tb) return ta.localeCompare(tb);
        // Tiebreak by priority
        // Tiebreak by priority — use `??` not `||` because priority=0 is
        // valid (it's the highest possible) and would otherwise be treated
        // as falsy → bumped to 999 → league pushed to the bottom.
        const pa = a.priority == null ? 999 : a.priority;
        const pb = b.priority == null ? 999 : b.priority;
        return pa - pb;
      });
      sorted.forEach((match, i) => {
        items.push({ type: 'match', match, isLast: i === sorted.length - 1, flat: true });
      });
      // Append LegalFooter as the last item — measured by Virtuoso like any
      // other row, so its full content (disclaimer + links + copyright) is
      // always rendered without clipping.
      items.push({ type: 'legal-footer' });
      return items;
    }
    leagueGroups.forEach(({ key, name, logo, matches: leagueMatches, isTop, defaultOpen }) => {
      // Determine effective open/closed: `defaultOpen` viene da lib/leagues.js
      // (regole hardcoded + override admin "Chiusa"). `isTop` resta per il
      // sorting/posizione. Le tap dell'utente vincono sempre via gli override.
      const open = defaultOpen ?? isTop;
      const isCollapsed = expandedOverrides.has(key)
        ? false
        : collapsedOverrides.has(key) || !open;
      items.push({ type: 'header', key, name, logo, count: leagueMatches.length, isCollapsed, isTop, defaultOpen: open });
      if (!isCollapsed) {
        leagueMatches.forEach((match, i) => {
          items.push({ type: 'match', match, isLast: i === leagueMatches.length - 1 });
        });
      }
    });
    items.push({ type: 'legal-footer' });
    return items;
  }, [leagueGroups, filteredMatches, sortByTime, showOnlyLive, collapsedOverrides, expandedOverrides]);

  const toggleLeague = useCallback((key, defaultOpen) => {
    // If the league is open by default, the toggle moves it to the
    // "collapsedOverrides" set; vice-versa for closed-by-default leagues.
    if (defaultOpen) {
      setCollapsedOverrides(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
      setExpandedOverrides(prev => { const n = new Set(prev); n.delete(key); return n; });
    } else {
      setExpandedOverrides(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
      setCollapsedOverrides(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, []);

  // Restore scroll position when returning from a match detail page.
  // 2026-08-09: usiamo `restoreStateFrom` di Virtuoso (passato inline al
  // componente) invece del vecchio window.scrollTo iterativo. Questo effect
  // resta solo per pulire lo snapshot se la data è cambiata.
  useEffect(() => {
    if (loading) return;
    let savedDate = null;
    try {
      savedDate = sessionStorage.getItem('dashboard_scroll_date');
    } catch { /* ignore */ }
    if (savedDate && savedDate !== selectedDate) {
      try {
        sessionStorage.removeItem('dashboard_virtuoso_state');
        sessionStorage.removeItem('dashboard_scroll_y');
        sessionStorage.removeItem('dashboard_scroll_date');
      } catch { /* ignore */ }
      // Drop any stale in-memory state too.
      setRestoredVirtuosoState(null);
    }
    // Pulisci lo snapshot dopo il primo restore così se l'utente naviga in
    // giro (nav bottom) e poi torna in Home non forza sempre lo scroll a
    // metà lista.
    if (restoredVirtuosoState && flatItems.length > 0) {
      const t = setTimeout(() => {
        try {
          sessionStorage.removeItem('dashboard_virtuoso_state');
          sessionStorage.removeItem('dashboard_scroll_y');
          sessionStorage.removeItem('dashboard_scroll_date');
        } catch { /* ignore */ }
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [loading, flatItems.length, selectedDate, restoredVirtuosoState]);

  const toggleLeagueFilter = (leagueKey) => {
    setDeselectedLeagues(prev => {
      const next = new Set(prev);
      if (next.has(leagueKey)) next.delete(leagueKey);
      else next.add(leagueKey);
      return next;
    });
  };

  const selectAllLeagues = () => setDeselectedLeagues(new Set());
  const deselectAllLeagues = () => setDeselectedLeagues(new Set(availableLeagues.map(l => l.key)));

  return (
    <PullToRefresh onRefresh={refetchMatches} className="min-h-screen bg-[#1A1A1A] pb-40" data-testid="dashboard-page">
      <EmailVerifyBanner />
      <NotificationReengagementBanner />
      <AfterPriorityPopups><NotificationProofOfLossModal /></AfterPriorityPopups>
      <VipTodayBanner />
      <div className="px-4 pt-8 pb-2">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="min-w-0">
            <p className="text-[10px] text-gray-600 uppercase tracking-widest font-bold flex items-center gap-2">
              Benvenuto
              {user?.is_vip && <span className="vip-badge px-2 py-0.5 rounded-full text-[9px] inline-flex items-center gap-1" data-testid="header-vip-badge"><Crown className="w-3 h-3" /> VIP</span>}
            </p>
            <h1 className="text-3xl font-black text-white leading-tight truncate" style={{fontFamily: 'Barlow Condensed'}}>{user?.name || 'Ospite'}</h1>
            {/* iter252 — Referral progress badge: pills ReferralProgressBadge
                is a small clickable chip right below the user name that
                answers "a che punto sono con gli inviti?" without the user
                asking admin. Only renders for authenticated non-VIP users
                after the /referral/me round-trip has resolved. */}
            {referralStatus && (
              <button
                onClick={() => navigate('/bonus')}
                className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all active:scale-95 ${
                  referralStatus.is_card_unlocked
                    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20'
                    : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/15'
                }`}
                data-testid="referral-progress-badge"
                aria-label={
                  referralStatus.is_card_unlocked
                    ? `Vantaggi attivi, ${referralStatus.days_remaining} giorni restanti`
                    : `${referralStatus.referrals_count} amici invitati`
                }
              >
                {referralStatus.is_card_unlocked ? (
                  <>
                    <Gift className="w-3 h-3" />
                    <span>Vantaggi attivi · {referralStatus.days_remaining}gg</span>
                  </>
                ) : (
                  <>
                    <Users className="w-3 h-3" />
                    <span>
                      {referralStatus.referrals_count} amici invitati
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
          {/* Favourite team's next match — only while "La mia selezione" is not shown */}
          {user && !showMySelection && (
            <FavoriteTeamCard liveById={liveScores?.byFixtureId} onOpen={(id) => navigate(`/match/${id}`)} />
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4 -mx-4 px-4 scrollbar-hide">
          {[-1, 0, 1, 2, 3].map((offset) => {
            const dateStr = getItalyDate(offset);
            const dateObj = new Date(dateStr + 'T12:00:00');
            const isSelected = dateStr === selectedDate;
            return (
              <button key={dateStr} onClick={() => setSelectedDate(dateStr)} className={`flex-shrink-0 flex items-baseline gap-1.5 px-4 py-2.5 rounded-xl transition-all ${isSelected ? "bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A] shadow-[0_0_20px_rgba(245,166,35,0.3)]" : "bg-[#242424] text-gray-500 hover:bg-[#2D2D2D]"}`} data-testid={`date-${dateStr}`}>
                <span className="text-[10px] uppercase tracking-wider font-bold">{offset === 0 ? "Oggi" : offset === 1 ? "Domani" : dateObj.toLocaleDateString("it-IT", { weekday: "short" })}</span>
                <span className="text-base font-black leading-none">{dateObj.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>
      
      <div className="px-4">
        {/* Search bar */}
        {showSearch && (
          <div className="mb-4 flex items-center gap-2 bg-[#242424] rounded-xl px-3 py-2 border border-gray-700/50 animate-[fadeIn_0.15s_ease-out]" data-testid="search-bar">
            <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca squadra..."
              className="flex-1 bg-transparent text-white text-sm outline-none placeholder-gray-600"
              data-testid="search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-gray-500 hover:text-white" data-testid="search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="text-gray-500 hover:text-white text-xs font-bold ml-1" data-testid="search-close">
              Chiudi
            </button>
          </div>
        )}

        {/* Filter bar */}
        <div className="relative mb-4 -mx-4">
          <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto scrollbar-hide px-4" data-testid="home-filter-row">
            {/* Search toggle */}
            <button
              onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchQuery(''); }}
              className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${showSearch || searchQuery ? 'bg-[#F5A623]/20 text-[#F5A623]' : 'bg-[#242424] text-gray-500'}`}
              data-testid="search-toggle-btn"
            >
              <Search className="w-3.5 h-3.5" />
            </button>

            {/* League filter */}
            <div className="flex-shrink-0" ref={filterRef}>
              <button
                onClick={() => setShowLeagueFilter(!showLeagueFilter)}
                className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${showLeagueFilter || activeLeagueFilterCount > 0 ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`}
                data-testid="league-filter-btn"
              >
                <SlidersHorizontal className="w-3 h-3" />
                {activeLeagueFilterCount > 0 ? `${availableLeagues.length - activeLeagueFilterCount}` : 'Filtri'}
              </button>

              {/* League filter dropdown — anchored to the LEFT edge of the
                  trigger so it always expands into available space (rightward)
                  on mobile where the button sits on the left side of the bar. */}
              {showLeagueFilter && (
                <div
                  className="absolute left-4 top-full mt-2 w-[min(18rem,calc(100vw-2rem))] max-h-80 bg-[#1E1E1E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]"
                  data-testid="league-filter-dropdown"
                >
                  <div className="sticky top-0 bg-[#1E1E1E] px-4 py-3 border-b border-gray-700/30 flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Campionati</span>
                    <div className="flex items-center gap-3">
                      <button onClick={selectAllLeagues} className="text-[10px] text-[#F5A623] font-bold" data-testid="league-select-all">Tutti</button>
                      <button onClick={deselectAllLeagues} className="text-[10px] text-gray-500 font-bold" data-testid="league-deselect-all">Nessuno</button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-64 overscroll-contain">
                    {availableLeagues.map(league => {
                      const isSelected = !deselectedLeagues.has(league.key);
                      return (
                        <button
                          key={league.key}
                          onClick={() => toggleLeagueFilter(league.key)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${isSelected ? 'bg-transparent' : 'bg-white/[0.02]'}`}
                          data-testid={`league-option-${league.key}`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? 'bg-[#F5A623]' : 'border border-gray-600'}`}>
                            {isSelected && <Check className="w-3 h-3 text-[#1A1A1A]" />}
                          </div>
                          {league.logo && <img src={league.logo} alt="" className="w-5 h-5 object-contain flex-shrink-0" />}
                          <span className={`text-xs font-medium truncate flex-1 text-left ${isSelected ? 'text-white' : 'text-gray-600'}`}>{league.name}</span>
                          <span className="text-[10px] text-gray-600 flex-shrink-0">{league.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Favorites */}
            {favoritesCount > 0 && (
              <button
                onClick={() => setShowFavorites(!showFavorites)}
                className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${showFavorites ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`}
                data-testid="favorites-filter-btn"
              >
                <Star className={`w-3 h-3 ${showFavorites ? 'fill-[#F5A623]' : ''}`} />
                {favoritesCount}
              </button>
            )}

            {/* Region filter */}
            <button onClick={() => setShowOnlyTopLeagues(!showOnlyTopLeagues)} className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${showOnlyTopLeagues ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`} data-testid="filter-leagues-btn">{showOnlyTopLeagues ? 'Principali' : 'Tutte'}</button>

            {/* Chronological sort toggle */}
            <button
              onClick={() => setSortByTime(!sortByTime)}
              className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${sortByTime ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`}
              data-testid="sort-by-time-btn"
              title={sortByTime ? 'Ordinamento cronologico attivo' : 'Ordina per orario'}
            >
              <Clock className="w-3 h-3" />
              Orario
            </button>

            {/* Live-only toggle */}
            <button
              onClick={() => { setShowOnlyLive(!showOnlyLive); if (!showOnlyLive) setShowOnlyUpcoming(false); }}
              className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${showOnlyLive ? 'bg-red-500/20 text-red-400 border border-red-500/40' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`}
              data-testid="filter-live-btn"
              title={showOnlyLive ? 'Mostra solo partite in corso' : 'Filtra partite live'}
            >
              <Radio className={`w-3 h-3 ${showOnlyLive ? 'animate-pulse' : ''}`} />
              Live
            </button>

            {/* Upcoming-only toggle (2026-02): NS/TBD only (hides LIVE + FT) */}
            <button
              onClick={() => { setShowOnlyUpcoming(!showOnlyUpcoming); if (!showOnlyUpcoming) setShowOnlyLive(false); }}
              className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${showOnlyUpcoming ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`}
              data-testid="filter-upcoming-btn"
              title={showOnlyUpcoming ? 'Mostra solo partite non ancora iniziate' : 'Filtra solo partite non ancora iniziate'}
            >
              <Clock className="w-3 h-3" />
              Prossime
            </button>

            {/* AI filter (bet type + confidence) */}
            <button
              onClick={() => setShowAiFilterPanel(!showAiFilterPanel)}
              className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${aiFiltersActive || showAiFilterPanel ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/40' : 'bg-[#242424] text-gray-500 border border-gray-700/50'}`}
              data-testid="filter-ai-btn"
              title="Filtra per pronostico o fiducia"
            >
              <Sparkles className="w-3 h-3" />
              IA
              {aiFiltersActive && <span className="text-[9px] bg-[#F5A623] text-black rounded-full w-4 h-4 flex items-center justify-center font-black ml-0.5">{aiFilterBetTypes.size + aiFilterConfidence.size}</span>}
            </button>
          </div>
        </div>

        {!isPremium && (
        <MySelectionButton
          hasSelection={!!shownSelection}
          active={showMySelection}
          importing={mySel.importing}
          onToggle={() => setShowMySelection((v) => !v)}
          onFile={importMySelection}
          onRequireLogin={user ? (shownSelection ? null : () => navigate('/vip?da=selezioni')) : () => navigate('/login?tab=register')}
        />
        )}

        {user && <NotificationsOffBar />}

        {/* AI filter panel — opens under the filter bar */}
        {showAiFilterPanel && (
          <div className="mb-3 rounded-2xl bg-[#141414] border border-[#F5A623]/30 p-3 space-y-3" data-testid="ai-filter-panel">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 font-bold">Tipo pronostico</p>
              <div className="flex flex-wrap gap-1.5">
                {['1', 'X', '2', 'Goal', 'No Goal', 'Over 2.5', 'Under 2.5'].map(bt => {
                  const active = aiFilterBetTypes.has(bt);
                  return (
                    <button
                      key={bt}
                      onClick={() => setAiFilterBetTypes(prev => {
                        const next = new Set(prev);
                        if (next.has(bt)) next.delete(bt); else next.add(bt);
                        return next;
                      })}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${active ? 'bg-[#F5A623] text-black border border-[#F5A623]' : 'bg-[#242424] text-gray-400 border border-gray-700/50 hover:border-[#F5A623]/40'}`}
                      data-testid={`ai-filter-bet-${bt.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    >
                      {bt}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 font-bold">Fiducia</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: 'Alta', color: 'text-green-400 border-green-400/40', active: 'bg-green-500 text-white border-green-500' },
                  { label: 'Media', color: 'text-[#F5A623] border-[#F5A623]/40', active: 'bg-[#F5A623] text-black border-[#F5A623]' },
                  { label: 'Bassa', color: 'text-red-400 border-red-400/40', active: 'bg-red-500 text-white border-red-500' },
                ].map(({ label, color, active: activeCls }) => {
                  const active = aiFilterConfidence.has(label);
                  return (
                    <button
                      key={label}
                      onClick={() => setAiFilterConfidence(prev => {
                        const next = new Set(prev);
                        if (next.has(label)) next.delete(label); else next.add(label);
                        return next;
                      })}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all border ${active ? activeCls : `bg-[#242424] ${color}`}`}
                      data-testid={`ai-filter-conf-${label.toLowerCase()}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-white/5">
              <p className="text-[10px] text-gray-500">
                {loadingAiFilter ? 'Carico pronostici...' : aiFiltersActive ? `${filteredMatches.length} partite con questi criteri` : 'Nessun filtro attivo'}
              </p>
              {aiFiltersActive && (
                <button
                  onClick={() => { setAiFilterBetTypes(new Set()); setAiFilterConfidence(new Set()); }}
                  className="text-[10px] text-[#F5A623] hover:text-[#F5A623]/80 font-bold flex items-center gap-1"
                  data-testid="ai-filter-reset-btn"
                >
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
            </div>
          </div>
        )}

        {/* Active filters summary */}
        {!loading && !showMySelection && (
        <div className="text-xs text-gray-600 mb-4 flex items-center gap-2 flex-wrap">
          {showFavorites ? (
            <span className="text-[#F5A623]">{filteredMatches.length} {filteredMatches.length === 1 ? 'preferito' : 'preferiti'}</span>
          ) : searchQuery ? (
            <span className="text-[#F5A623]">{filteredMatches.length} risultat{filteredMatches.length === 1 ? 'o' : 'i'} per "{searchQuery}"</span>
          ) : (
            <>
              {loadingAiFilter && aiFiltersActive ? (
                <span className="text-[#F5A623] flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#F5A623] animate-pulse" />
                  Applico filtri IA sul giorno…
                </span>
              ) : (
                <>{filteredMatches.length} {filteredMatches.length === 1 ? 'partita' : 'partite'}{matches.length > filteredMatches.length && <span className="text-gray-700"> / {matches.length} totali</span>}</>
              )}
              {/* Detailed exclusion breakdown (2026-02): when the user has
                  an active filter, show WHY some matches were hidden so the
                  count difference is transparent. */}
              {filterBreakdown && (filterBreakdown.finished > 0 || (showOnlyUpcoming && filterBreakdown.live > 0)) && (
                <span className="text-[10px] text-gray-500" data-testid="filter-breakdown">
                  ({[
                    filterBreakdown.finished > 0 ? `${filterBreakdown.finished} conclus${filterBreakdown.finished === 1 ? 'a' : 'e'}` : null,
                    showOnlyUpcoming && filterBreakdown.live > 0 ? `${filterBreakdown.live} live` : null,
                  ].filter(Boolean).join(', ')} escluse)
                </span>
              )}
            </>
          )}
          {activeLeagueFilterCount > 0 && !showFavorites && (
            <button onClick={selectAllLeagues} className="text-[10px] text-[#F5A623]/70 hover:text-[#F5A623] flex items-center gap-1" data-testid="clear-league-filter">
              <X className="w-3 h-3" /> Reset filtri
            </button>
          )}
        </div>
        )}
        {!loading && !showMySelection && (
          <div className="mb-4 -mt-2" data-testid="home-click-match-heading">
            <h2 className="text-lg font-black text-white flex items-center gap-2" style={{fontFamily: 'Barlow Condensed'}}><Trophy className="w-5 h-5 text-[#F5A623]" />CLICCA UNA PARTITA PER L'ANALISI</h2>
            <p className="text-xs text-gray-500 mt-1">Seleziona una partita e leggi l'approfondimento</p>
          </div>
        )}


        {/* Only show error if we have NO data at all - never show technical errors */}
        {matchesError && matches.length === 0 && !loading && (
          <div className="mb-4 p-4 rounded-2xl bg-[#242424] border border-[#333] text-center">
            <p className="text-sm text-gray-400 mb-2">Impossibile caricare le partite. Controlla la connessione.</p>
            <button onClick={() => refetchMatches()} className="px-4 py-2 rounded-xl bg-[#F5A623] text-[#1A1A1A] text-xs font-bold" data-testid="retry-matches-btn">Riprova</button>
          </div>
        )}
        {showMySelection && shownSelection ? (
          <>
            <MySelectionList
              selection={shownSelection}
              importing={mySel.importing}
              onFile={importMySelection}
              onToggleNotify={() => (mySel.selection?.notify ? mySel.setNotify(false) : askNotify(true))}
              onRemove={async () => { await mySel.remove(); setShowMySelection(false); }}
              onRemovePick={async (id) => { if (!(await mySel.removePick(id))) setShowMySelection(false); }}
            />
            <LegalFooter />
          </>
        ) : loading ? (
          <MatchSkeleton />
        ) : filteredMatches.length === 0 ? (
          <>
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-[#2D2D2D]/50 flex items-center justify-center">
              {searchQuery ? <Search className="w-10 h-10 text-[#404040]" /> : showFavorites ? <Star className="w-10 h-10 text-[#404040]" /> : <Trophy className="w-10 h-10 text-[#404040]" />}
            </div>
            <p className="text-gray-500 font-medium">
              {searchQuery ? `Nessuna squadra trovata per "${searchQuery}"` : showFavorites ? 'Nessun preferito per questa data' : showOnlyLive ? 'Nessuna partita live in questo momento' : activeLeagueFilterCount > 0 ? 'Nessuna partita con i filtri selezionati' : showOnlyTopLeagues ? 'Nessuna partita principale' : 'Nessuna partita per questa data'}
            </p>
            {searchQuery && <button onClick={() => setSearchQuery('')} className="mt-4 text-sm text-[#F5A623] hover:underline">Cancella ricerca</button>}
            {showFavorites && <button onClick={() => setShowFavorites(false)} className="mt-4 text-sm text-[#F5A623] hover:underline">Mostra tutte le partite</button>}
            {showOnlyLive && !showFavorites && !searchQuery && <button onClick={() => setShowOnlyLive(false)} className="mt-4 text-sm text-[#F5A623] hover:underline">Mostra tutte le partite</button>}
            {!showFavorites && !searchQuery && activeLeagueFilterCount > 0 && <button onClick={selectAllLeagues} className="mt-4 text-sm text-[#F5A623] hover:underline">Mostra tutti i campionati</button>}
            {!showFavorites && !searchQuery && activeLeagueFilterCount === 0 && showOnlyTopLeagues && matches.length > 0 && <button onClick={() => setShowOnlyTopLeagues(false)} className="mt-4 text-sm text-[#F5A623] hover:underline">Mostra tutte le {matches.length} partite</button>}
          </div>
          <LegalFooter />
          </>
        ) : (
          <Virtuoso
            ref={virtuosoRef}
            useWindowScroll
            data={flatItems}
            // Se al mount abbiamo uno stato salvato dalla precedente Home
            // (dopo un ritorno con "indietro" dal dettaglio partita), Virtuoso
            // ripristina posizione + item sizes in modo pixel-perfect.
            restoreStateFrom={restoredVirtuosoState || undefined}
            // Wide buffer so fast flings land on rows (and logos) already rendered.
            increaseViewportBy={{ top: 800, bottom: 1600 }}
            // 2026-08-09: `defaultItemHeight` evita layout thrashing durante
            // lo scroll rapido — Virtuoso può stimare l'altezza totale senza
            // dover misurare ogni item. 68px = altezza media della row.
            defaultItemHeight={68}
            // Stable keys → Virtuoso reuses the same DOM nodes when the
            // matches list refetches every 60s, instead of unmounting and
            // recreating every visible row. This is what makes scrolling
            // smooth even while polling is happening in the background.
            computeItemKey={(_idx, item) => {
              if (!item) return `idx-${_idx}`;
              if (item.type === 'header') return `h-${item.key}`;
              if (item.type === 'legal-footer') return 'legal-footer';
              return `m-${item.match.fixture_id}`;
            }}
            itemContent={(index, item) => {
              if (item.type === 'legal-footer') {
                return <LegalFooter />;
              }
              if (item.type === 'header') {
                return (
                  <div className={`bg-[#242424] ${item.isCollapsed ? 'rounded-xl mt-3' : 'rounded-t-xl mt-3'} overflow-hidden`}>
                    <button
                      type="button"
                      onClick={() => toggleLeague(item.key, item.defaultOpen)}
                      className="w-full text-left cursor-pointer active:bg-[#2D2D2D] transition-colors"
                      data-testid={`league-toggle-${item.key}`}
                    >
                      <LeagueHeader
                        leagueName={item.name}
                        leagueLogo={item.logo}
                        matchCount={item.count}
                        isCollapsed={item.isCollapsed}
                      />
                    </button>
                  </div>
                );
              }
              if (item.flat) {
                // Chronological flat view: each match is a standalone rounded card
                return (
                  <div className="bg-[#242424] rounded-xl mb-1.5 overflow-hidden">
                    <MatchRow
                      match={item.match}
                      onNavigate={handleMatchClick}
                      isFavorite={!!favorites[item.match.fixture_id]}
                      onToggleFavorite={onStarTap}
                      isVip={isVip}
                      isFollowing={matchFollows.isFollowing(item.match.fixture_id)}
                    />
                  </div>
                );
              }
              return (
                <div className={`bg-[#242424] ${item.isLast ? 'rounded-b-xl mb-1' : ''}`}>
                  <MatchRow
                    match={item.match}
                    onNavigate={handleMatchClick}
                    isFavorite={!!favorites[item.match.fixture_id]}
                    onToggleFavorite={onStarTap}
                    isVip={isVip}
                    isFollowing={matchFollows.isFollowing(item.match.fixture_id)}
                  />
                </div>
              );
            }}
          />
        )}
      </div>
      
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="notification-modal">
          <div className="bg-[#242424] rounded-3xl p-6 w-full max-w-sm text-center relative animate-[fadeIn_0.3s_ease-out]">
            <button onClick={dismissNotificationModal} className="absolute top-4 right-4 text-gray-500 hover:text-white" data-testid="close-notification-modal"><X className="w-5 h-5" /></button>
            <div className="w-20 h-20 rounded-full bg-[#F5A623]/20 flex items-center justify-center mx-auto mb-5"><Bell className="w-10 h-10 text-[#F5A623]" /></div>
            <h2 className="text-xl font-black text-white mb-3" style={{fontFamily: 'Barlow Condensed'}}>ATTIVA LE NOTIFICHE</h2>
            <p className="text-gray-400 text-sm mb-5">Ricevi subito i nostri <span className="text-[#F5A623] font-bold">pronostici VIP</span> e le notifiche sulle <span className="text-green-400 font-bold">vincite</span>!</p>
            {notificationPermission === 'denied' ? (
              <>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-left">
                  <p className="text-red-400 text-xs font-bold mb-2">Le notifiche sono bloccate</p>
                  <p className="text-gray-400 text-xs">Per attivarle:</p>
                  <ol className="text-gray-400 text-xs mt-2 space-y-1"><li>1. Tocca i 3 puntini in alto</li><li>2. Impostazioni - Notifiche</li><li>3. Trova questo sito e seleziona "Consenti"</li></ol>
                </div>
                <button onClick={dismissNotificationModal} className="w-full h-12 bg-gray-700 rounded-xl font-bold text-white">Ho capito</button>
              </>
            ) : (
              <>
                <button onClick={handleEnableNotifications} disabled={requestingPermission} className="w-full h-14 btn-primary rounded-xl font-bold flex items-center justify-center gap-2 mb-3" data-testid="enable-notifications-btn">
                  {requestingPermission ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Bell className="w-5 h-5" />Attiva Notifiche</>}
                </button>
                <button onClick={dismissNotificationModal} className="text-gray-500 text-sm hover:text-gray-300">Non ora</button>
              </>
            )}
          </div>
        </div>
      )}
      
      <BottomNav />
      {/* First-visit tutorial: highlights a random non-started match in the
          filtered/sorted list and blocks the rest of the UI until the user
          actually opens a match. Persists "seen" in localStorage. */}
      <AfterPriorityPopups>
      <OnboardingTutorial
        matches={flatItems.filter(it => it.type === 'match').map(it => it.match)}
        isLoading={loading}
        selectedDate={selectedDate}
      />
      </AfterPriorityPopups>
      {/* iter239 — Popup codice amico post-Google-login (saltabile).
          Si apre solo se c'è il flag `pendingReferralPopup` in localStorage,
          settato dal callback OAuth. L'utente può saltare senza penalità. */}
      <CardQuotaReachedModal
        open={importQuotaOpen}
        onClose={() => setImportQuotaOpen(false)}
        onOpenInvite={() => setImportInviteOpen(true)}
        testId="import-quota-reached-modal"
        description={<>Hai usato i <strong className="text-white">3 import selezione</strong> gratuiti di oggi. Per caricarne altre, scegli una delle opzioni qui sotto.</>}
        inviteSubtitle="7 giorni gratis per ogni amico"
      />
      <InviteFriendsModal open={importInviteOpen} onClose={() => setImportInviteOpen(false)} />
      {mySelTour && <AfterPriorityPopups><MySelectionTour onFinish={finishMySelTour} /></AfterPriorityPopups>}
      <NotifyAskModal
        open={!!notifyAsk}
        pushState={notifyAsk}
        onAnswer={(ok) => { setNotifyAsk(null); if (ok) mySel.setNotify(true); }}
      />
      <StarChoiceModal
        open={!!starChoiceId}
        onClose={() => setStarChoiceId(null)}
        onFavorite={() => toggleFavorite(starChoiceId)}
        onAddToSelection={(market) => mySel.addPick(starChoiceId, market)}
        following={matchFollows.isFollowing(starChoiceId)}
        onFollow={() => {
          const m = (matches || []).find((x) => String(x.fixture_id) === String(starChoiceId));
          if (m) matchFollows.toggle(m);
        }}
      />
      <GoogleReferralCodeModal
        open={showReferralPopup && !priorityBlocking}
        onClose={() => {
          setShowReferralPopup(false);
          try { localStorage.removeItem('pendingReferralPopup'); } catch {}
        }}
      />
    </PullToRefresh>
  );
};

export default Dashboard;
